import { inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { TableMemory } from '@festo-ax/smartenance-shared-ui';
import { LOCAL_STORAGE, StorageService, StorageType } from 'app/services/storage/storage.service';
import { ApplicationStore } from 'app/state-management/application/application.store';
import { fromStorable, isKeyOfUser, storageKey, toStorable } from './global-table-memory.util';

type TableMemoryMap = Record<string, TableMemory>;

/**
 * App-wide table view-state, one entry per logical table key. Tables reach it
 * only through `TableMemoryPort`, which `provideTableMemory()` binds here.
 *
 * Backed by localStorage, one entry per user and table: two tabs cannot overwrite
 * each other's unrelated tables, a corrupt entry costs one table rather than all
 * of them, and the next user on a shared machine does not inherit the previous
 * user's filters. Scoping the key is display hygiene, not isolation — anything in
 * localStorage is readable by anyone with the browser profile.
 *
 * Snapshots are stored untyped — the map holds tables with unrelated filter
 * shapes, and `withTableMemory` casts back to its own `F` on read.
 */
export const GlobalTableMemoryStore = signalStore(
  { providedIn: 'root' },
  withState<{ views: TableMemoryMap }>({ views: {} }),
  withMethods((
    store,
    storage = inject(StorageService),
    localStorageRef = inject(LOCAL_STORAGE),
    appStore = inject(ApplicationStore),
  ) => {
    // The storage key doubles as the in-memory key, so one user's snapshot can
    // never be served from memory to another.
    const keyOf = (name: string): string => storageKey(appStore.userId(), name);

    const cache = (key: string, view: TableMemory): void => {
      patchState(store, (state) => ({ views: { ...state.views, [key]: view } }));
    };

    return {
      /**
       * Falls back to storage on a miss, so a table opened later picks up what another tab wrote.
       *
       * Writes no state, deliberately. Hydration runs in a store's `onInit`, and a
       * store may be constructed while a template binding is being evaluated —
       * inside that reactive context any signal write throws NG0600. The cache is
       * filled by `write` alone; `withTableMemory` writes current state through
       * right after hydration, so a miss costs one storage read, not a stale cache.
       */
      read(name: string): TableMemory | undefined {
        const key: string = keyOf(name);
        const cached: TableMemory | undefined = store.views()[key];

        if (cached) {
          return cached;
        }

        return fromStorable(storage.get<unknown>(key, StorageType.Local));
      },

      write(name: string, view: TableMemory): void {
        const key: string = keyOf(name);
        cache(key, view);

        try {
          storage.set(key, toStorable(view), StorageType.Local);
        } catch {
          // Quota exceeded or storage blocked: the in-memory copy still serves this session.
        }
      },

      clear(name: string): void {
        const key: string = keyOf(name);

        patchState(store, (state) => {
          const views: TableMemoryMap = { ...state.views };
          delete views[key];

          return { views };
        });

        storage.delete(key, StorageType.Local);
      },

      /**
       * Forgets every table's view-state for the current user. The app-wide reset
       * point for logout or similar.
       *
       * Another user's entries are theirs — clearing this user's memory has no
       * business deleting them.
       */
      clearAll(): void {
        patchState(store, { views: {} });

        if (!localStorageRef) {
          return;
        }

        const userId: string = appStore.userId();

        Object.keys(localStorageRef)
          .filter((key: string) => isKeyOfUser(key, userId))
          .forEach((key: string) => localStorageRef.removeItem(key));
      },
    };
  }),
);
